import { createContext, useContext, useReducer, ReactNode } from "react";
import { Language } from "../Constants/LanguageConstants";
import { IDocumentModel } from "../Models/DocumentManifestModel";

// Document interface
// export interface IDocument {
//   id: string;
//   name: string;
//   lastModified?: Date;
//   size?: number;
// }

// State interface
export interface IDocumentState {
  documents: IDocumentModel[];
  selectedDocumentFile: string | null;
  loading: boolean;
  error: string | null;
  searchTerm: string;
}

// Action types
export type DocumentAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_DOCUMENTS"; payload: IDocumentModel[] }
  | { type: "ADD_DOCUMENT"; payload: IDocumentModel }
  | { type: "REMOVE_DOCUMENT"; payload: string }
  | { type: "SELECT_DOCUMENT"; payload: string | null }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_SEARCH_TERM"; payload: string }
  | { type: "CLEAR_ALL" };

// Initial state
const initialState: IDocumentState = {
  documents: [],
  selectedDocumentFile: null,
  loading: false,
  error: null,
  searchTerm: '',
};

// Reducer function
export const documentsReducer = (state: IDocumentState, action: DocumentAction): IDocumentState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_DOCUMENTS':
      return { ...state, documents: action.payload, loading: false, error: null };
    case 'ADD_DOCUMENT':
      return { ...state, documents: [...state.documents, action.payload] };
    case 'REMOVE_DOCUMENT':
      return {
        ...state,
        documents: state.documents.filter(doc => doc.file !== action.payload),
        selectedDocumentFile: state.selectedDocumentFile === action.payload ? null : state.selectedDocumentFile,
      };
    case 'SELECT_DOCUMENT':
      return { ...state, selectedDocumentFile: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_SEARCH_TERM':
      return { ...state, searchTerm: action.payload };
    case 'CLEAR_ALL':
      return initialState;
    default:
      return state;
  }
};

// Context interface
export interface IDocumentContext {
  state: IDocumentState;
  dispatch: React.Dispatch<DocumentAction>;
  // Computed values
  filteredDocuments: IDocumentModel[];
  selectedDocument: IDocumentModel | null;
  // Helper functions
  initializeDocuments: (lang: Language) => void;
  selectDocument: (id: string | null) => void;
  addDocument: (document: IDocumentModel) => void;
  removeDocument: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  search: (term: string) => void;
  fetchMarkdownContent: (filePath: string) => Promise<string>;
}

// Create context
export const DocumentsContext = createContext<IDocumentContext | null>(null);

// Custom hook for using the context
export const useDocuments = () => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error('useDocuments must be used within a DocumentsProvider');
  }
  return context;
};
